"use client";

/**
 * Renders sanitized HTML content using `sanitize-html`.
 * Centralizes XSS prevention for all dangerouslySetInnerHTML usages.
 *
 * Uses `sanitize-html` (pure JS) rather than `isomorphic-dompurify` so the public
 * landing-page server render doesn't drag in jsdom — its `html-encoding-sniffer`
 * → `@exodus/bytes` chain does a CJS `require()` of an ESM module and throws
 * `ERR_REQUIRE_ESM` on Vercel, 500-ing the whole page (DEV-3674). Mirrors the
 * whitepapers `SanitizedHtml`, which switched for the same reason.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import type React from "react";
import sanitizeHtml from "sanitize-html";
import { isAnchorLink, isEmailLink, isExternalLink } from "../../../../_logic/smart-link-utils";
import { getStickyHeaderOffset } from "../../../../_logic/utils/scroll-utils";
import { TIPTAP_TABLE_CLASS } from "../../../../_logic/tiptap-table-extensions";
import { usePublicViewContext } from "../../../public/PublicViewContext";

/** Allowed HTML tags for TipTap prose content */
const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s",
  "a",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "blockquote",
  "code",
  "pre",
  "sub",
  "sup",
  "mark",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "colgroup",
  "col",
];

/** Allowed CSS properties for inline styles — blocks CSS exfiltration and UI redress attacks.
 *  Property-name allowlist only (values unrestricted), matching the prior DOMPurify behavior. */
const ALLOWED_CSS_PROPERTIES = [
  "color",
  "background-color",
  "text-align",
  "font-weight",
  "font-style",
  "text-decoration",
  "width",
  "min-width",
  "vertical-align",
] as const;

/** sanitize-html `allowedStyles` map: each allowed property accepts any value. */
const ALLOWED_STYLES: Record<string, RegExp[]> = Object.fromEntries(
  ALLOWED_CSS_PROPERTIES.map((property) => [property, [/.*/]])
);

/**
 * sanitize-html options replicating the prior DOMPurify config:
 * - tag + attribute allowlists (table-only attrs scoped to table-family tags)
 * - inline `style` filtered to {@link ALLOWED_CSS_PROPERTIES}
 * - `class` only on `<table>`, and only the `tiptap-table` token
 * - links limited to http(s)/mailto plus relative/anchor; `target="_blank"` forced to rel=noopener
 * - email links (`#__email-{id}`) unwrapped to plain text, since they only send from CTA buttons
 */
const SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: ALLOWED_TAGS,
  allowedAttributes: {
    a: ["href", "target", "rel"],
    table: ["width"],
    th: ["colspan", "rowspan", "colwidth", "width"],
    td: ["colspan", "rowspan", "colwidth", "width"],
    col: ["span", "width"],
    colgroup: ["span"],
    "*": ["style"],
  },
  // Only `<table>` keeps a class, and only `tiptap-table` (drives table.tiptap-table styling).
  allowedClasses: { table: [TIPTAP_TABLE_CLASS] },
  allowedStyles: { "*": ALLOWED_STYLES },
  // http(s)/mailto schemes; relative (`/page`) and anchor (`#section`) links have no scheme and pass.
  allowedSchemes: ["http", "https", "mailto"],
  // `excludeTag` drops only the email link's `<a>` and keeps its children. Renaming the tag in
  // `transformTags` instead corrupts the next sibling link's closing tag (sanitize-html leaves a
  // stale rename mapping behind when the renamed tag is then discarded).
  exclusiveFilter: (frame) => (frame.tag === "a" && isEmailLink(frame.attribs.href) ? "excludeTag" : false),
  transformTags: {
    a: (tagName, attribs) => {
      const next = { ...attribs };
      // Force noopener/noreferrer on _blank links to prevent reverse-tabnabbing.
      if (next.target === "_blank") next.rel = "noopener noreferrer";
      return { tagName, attribs: next };
    },
  },
};

interface SanitizedHtmlProps {
  /** Raw HTML string to sanitize and render */
  html: string;
  /** Tailwind classes for the wrapper div */
  className?: string;
  /**
   * Called for every non-anchor link click, before the external-link (http(s)) check —
   * so an app-internal link (e.g. a form link) is recognized even when authored as a
   * fully-qualified URL rather than a relative path. Return `true` if the caller took
   * over navigation (e.g. opened an in-place modal); this prevents the native `<a>`
   * navigation. Return `false`/omit the prop to fall through to the existing
   * external-link/native-navigation behavior, unchanged from today.
   */
  onInternalLinkClick?: (href: string) => boolean;
}

/**
 * Safely renders HTML content after `sanitize-html` sanitization.
 * Renders the initial sanitized HTML via dangerouslySetInnerHTML so SSR/first paint
 * includes inline styles like text-align. After mount, subsequent prop changes are
 * applied via ref.innerHTML to preserve Google Translate's DOM modifications between
 * renders (React doesn't track ref-based DOM mutations).
 */
export function SanitizedHtml({
  html,
  className = "prose prose-gray max-w-none",
  onInternalLinkClick,
}: SanitizedHtmlProps): React.ReactElement {
  // Hooks
  const { isPublic } = usePublicViewContext();

  // Refs
  const ref = useRef<HTMLDivElement>(null);
  // Frozen initial HTML — used for SSR + first client render. Never updated after mount,
  // so React's dangerouslySetInnerHTML diff is a no-op on subsequent re-renders and
  // doesn't clobber Google Translate's spans.
  const initialHtmlRef = useRef<string | null>(null);
  const lastAppliedRef = useRef<string | null>(null);

  // Derived values — memoize sanitization to avoid re-running on every render
  const sanitizedHtml = useMemo(() => sanitizeHtml(html, SANITIZE_OPTIONS), [html]);

  // Capture the first sanitized value for the initial render
  if (initialHtmlRef.current === null) {
    initialHtmlRef.current = sanitizedHtml;
    lastAppliedRef.current = sanitizedHtml;
  }

  // Handlers — intercept link clicks for smart routing (public view only)
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isPublic) return;

      const target = (e.target as HTMLElement).closest("a");
      if (!target) return;

      const href = target.getAttribute("href");
      if (!href) return;

      if (isAnchorLink(href)) {
        e.preventDefault();
        const id = href.slice(1);
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset(24);
          window.scrollTo({ top, behavior: "smooth" });
        }
      } else if (onInternalLinkClick?.(href)) {
        e.preventDefault();
      } else if (isExternalLink(href)) {
        e.preventDefault();
        window.open(href, "_blank", "noopener,noreferrer");
      }
    },
    [isPublic, onInternalLinkClick]
  );

  // Effects — apply post-mount content updates via ref so React doesn't overwrite Google Translate
  useEffect(() => {
    if (ref.current && sanitizedHtml !== lastAppliedRef.current) {
      ref.current.innerHTML = sanitizedHtml;
      lastAppliedRef.current = sanitizedHtml;
    }
  }, [sanitizedHtml]);

  // Render — initial HTML is frozen so React only applies dangerouslySetInnerHTML once
  return (
    <div
      ref={ref}
      className={className}
      onClick={handleClick}
      dangerouslySetInnerHTML={{ __html: initialHtmlRef.current }}
    />
  );
}
